import * as React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  DataGrid,
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  GridColDef,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridSlotProps,
  GridToolbarContainer,
} from '@mui/x-data-grid';

const BRAND_GREEN = '#1D9E75';

const dataGridSx = {
  border: 'none',
  background: 'transparent',
  fontFamily: "'DM Mono', monospace",
  '& .MuiDataGrid-columnHeader': {
    background: `${BRAND_GREEN} !important`,
    color: '#ffffff !important',
    fontFamily: "'Syne', sans-serif",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: '#ffffff !important',
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
  '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
  '& .MuiDataGrid-row': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    background: 'transparent',
  },
  '& .MuiDataGrid-row:hover': {
    background: 'rgba(255,255,255,0.02)',
  },
  '& .MuiDataGrid-cell': {
    border: 'none',
    background: 'transparent',
    fontFamily: "'DM Mono', monospace",
    fontSize: 13,
    color: 'var(--color-text-primary)',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid rgba(255,255,255,0.06)',
    background: 'transparent',
  },
  '& .MuiTablePagination-root': {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    color: 'var(--color-text-secondary)',
  },
  '& .MuiDataGrid-toolbarContainer': {
    justifyContent: 'flex-end',
    pb: 1,
  },
};

let idCounter = 0;

interface ReusableDataGridProps {
  defaultRow: Record<string, number | string | boolean | null | number[]>;
  columns: GridColDef[];
  height?: number | string;
  width?: number | string;
  initialRows?: GridRowsProp;
  onSaveRow?: (row: GridRowModel) => Promise<GridRowModel>;
  onDeleteRow?: (row: GridRowModel) => Promise<void>;
}

function EditToolbar({ setRows, setRowModesModel, defaultRow }: GridSlotProps['toolbar'] & { defaultRow: Record<string, any> }) {
  const handleClick = () => {
    idCounter += 1;
    setRows((oldRows: any) => [{ ...defaultRow, id: idCounter, isNew: true }, ...oldRows]);
    setRowModesModel((oldModel: any) => ({
      ...oldModel,
      [idCounter]: { mode: GridRowModes.Edit, fieldToFocus: Object.keys(defaultRow)[0] },
    }));
  };

  return (
    <GridToolbarContainer sx={{ justifyContent: 'flex-end', pb: 1 }}>
      <Tooltip title="Add rule">
        <Box
          component="button"
          onClick={handleClick}
          sx={{
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'transparent',
            cursor: 'pointer',
            borderRadius: 1.5,
            p: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: BRAND_GREEN,
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: BRAND_GREEN,
              background: 'rgba(29,158,117,0.08)',
            },
          }}
        >
          <AddIcon sx={{ fontSize: '14px !important', padding: '0 !important' }} />
          Add
        </Box>
      </Tooltip>
    </GridToolbarContainer>
  );
}

export default function ReusableDataGrid({
  defaultRow,
  columns,
  height = '100%',
  width = '100%',
  initialRows,
  onSaveRow,
  onDeleteRow,
}: ReusableDataGridProps) {
  const [rows, setRows] = React.useState<GridRowsProp>([]);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick   = (id: GridRowId) => () => setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  const handleSaveClick   = (id: GridRowId) => () => setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });
    const editedRow = rows.find(r => r.id === id);
    if (editedRow?.isNew) setRows(rows.filter(r => r.id !== id));
  };

  const handleDeleteClick = (id: GridRowId) => async () => {
    const row = rows.find(r => r.id === id);
    if (onDeleteRow && row && !row.isNew) await onDeleteRow(row);
    setRows(rows.filter(r => r.id !== id));
  };

  const processRowUpdate = async (newRow: GridRowModel) => {
    let savedRow = { ...newRow, isNew: false };
    if (onSaveRow) savedRow = await onSaveRow(newRow);
    setRows(rows.map(r => r.id === newRow.id ? savedRow : r));
    return savedRow;
  };

  React.useEffect(() => {
    if (initialRows) {
      setRows(initialRows);
      const maxId = initialRows.reduce((max, row) => Math.max(max, row.id ?? 0), 0);
      idCounter = maxId;
    }
  }, [initialRows]);

  const iconSx = (color: string) => ({
    color: `${color} !important`,
    '& svg': { fontSize: '16px !important', padding: '0 !important' },
  });

  const columnsWithActions: GridColDef[] = [
    ...columns,
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      minWidth: 100,
      flex: 0.5,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem icon={<SaveIcon />}   label="Save"   onClick={handleSaveClick(id)}   sx={iconSx(BRAND_GREEN)} />,
            <GridActionsCellItem icon={<CancelIcon />} label="Cancel" onClick={handleCancelClick(id)} sx={iconSx('var(--color-text-secondary)')} />,
          ];
        }
        return [
          <GridActionsCellItem icon={<EditIcon />}   label="Edit"   onClick={handleEditClick(id)}   sx={iconSx(BRAND_GREEN)} />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={handleDeleteClick(id)} sx={iconSx('#993C1D')} />,
        ];
      },
    },
  ];

  return (
    <Box sx={{ width, height, minHeight: height }}>
      <DataGrid
        rows={rows}
        columns={columnsWithActions}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        processRowUpdate={processRowUpdate}
        onRowEditStop={handleRowEditStop}
        pageSizeOptions={[5, 10, 25, { value: -1, label: 'All' }]}
        initialState={{ pagination: { paginationModel: { pageSize: -1 } } }}
        slots={{ toolbar: EditToolbar }}
        slotProps={{ toolbar: { setRows, setRowModesModel, defaultRow } }}
        showToolbar
        autoHeight={false}
        sx={{ ...dataGridSx, height: '100%' }}
      />
    </Box>
  );
}
